import * as React from "react";
import { DysonSphere } from "@/components/ui/DysonSphere";
import { Icon } from "./LandingIcons";

interface Audience {
  key: string;
  eye: string;
  name: string;
  title: string;
  desc: string;
  bullets: string[];
  visual: "you" | "builders" | "teams";
}

const AUDIENCES: Audience[] = [
  {
    key: "you",
    eye: "For you",
    name: "Daily life, on autopilot",
    title: "Your second brain runs the whole house.",
    desc: "Schedules, errands, travel, finances, conversations. One voice across phone, watch, browser and home — that remembers what you said last Tuesday.",
    bullets: [
      "Voice-first across iPhone, Android, watch, web, desktop",
      "Persistent memory of your routines, people and preferences",
      "200+ everyday skills — calendar, email, travel, smart-home",
      "Offline-capable on device; encrypted sync across the rest",
    ],
    visual: "you",
  },
  {
    key: "builders",
    eye: "For builders",
    name: "Ship agents to 15k+ users",
    title: "Build once. Earn from the marketplace.",
    desc: "A TypeScript SDK, a remote IDE, hot-reload across every Omni device, and a marketplace with an 80/20 revenue split — in your favor.",
    bullets: [
      "Type-safe SDK with full IDE intellisense",
      "Skills, MCP servers, full agents — three layers of abstraction",
      "`omni publish` — security review in 24h, marketplace exposure",
      "Keep 80% of revenue. Net-30 payouts. No platform tax shenanigans.",
    ],
    visual: "builders",
  },
  {
    key: "teams",
    eye: "For teams",
    name: "Agents that do the work",
    title: "Production AI workflows, on your infra.",
    desc: "Self-host the entire stack. Wire it to your IdP. Audit every keystroke. Or use our managed cloud with dedicated tenancy — your call.",
    bullets: [
      "Self-host with one Docker command. AWS, GCP, Azure, bare metal",
      "SSO via SAML 2.0 / OIDC · SCIM provisioning · RBAC down to skills",
      "SOC 2, HIPAA-ready, GDPR mode, full audit trail",
      "Dedicated infra · 24/7 SLA · solutions engineer on call",
    ],
    visual: "teams",
  },
];

interface DeviceItem {
  l: string;
  d: string;
}

interface TeamRow {
  l: string;
  k: string;
  v: string;
}

function AudVisual({ which }: { which: "you" | "builders" | "teams" }) {
  if (which === "you") {
    const items: DeviceItem[] = [
      { l: "phone",  d: "iPhone"   },
      { l: "watch",  d: "Watch S10" },
      { l: "web",    d: "omni.app"  },
      { l: "mac",    d: "macOS"    },
      { l: "home",   d: "HomePod"  },
      { l: "car",    d: "CarPlay"  },
    ];
    return (
      <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16, height: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, paddingBottom: 16, borderBottom: "1px dashed var(--line)" }}>
          <DysonSphere size={56} mode="idle" />
          <div>
            <div className="mono" style={{ fontSize: 10, letterSpacing: ".16em", textTransform: "uppercase", color: "var(--accent)" }}>SYNCED</div>
            <div style={{ fontSize: 13, color: "var(--fg-muted)" }}>1 brain · 6 surfaces · 0ms drift</div>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {items.map(i => (
            <div key={i.l} style={{ border: "1px solid var(--line)", padding: "10px 12px", borderRadius: 8, background: "var(--bg-card)" }}>
              <div className="mono" style={{ fontSize: 10, color: "var(--fg-faint)", letterSpacing: ".1em", textTransform: "uppercase" }}>{i.l}</div>
              <div style={{ fontSize: 13, color: "var(--fg)" }}>{i.d}</div>
            </div>
          ))}
        </div>
        <div className="mono" style={{ fontSize: 11, color: "var(--fg-faint)", marginTop: "auto", paddingTop: 12, borderTop: "1px dashed var(--line)" }}>
          memory.facts · 4,182 entries · last sync 13s ago
        </div>
      </div>
    );
  }

  if (which === "builders") {
    return (
      <div style={{ padding: 0, height: "100%", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", borderBottom: "1px solid var(--line)", padding: "0 4px" }}>
          {["weather-pro.ts", "manifest.json"].map((t, i) => (
            <div key={t} className="mono" style={{
              padding: "10px 14px", fontSize: 11, color: i === 0 ? "var(--fg)" : "var(--fg-faint)",
              borderBottom: i === 0 ? "2px solid var(--accent)" : "2px solid transparent", marginBottom: -1,
            }}>{t}</div>
          ))}
        </div>
        <pre className="mono" style={{ margin: 0, padding: 18, fontSize: 12, lineHeight: 1.65, color: "var(--fg-muted)", flex: 1 }}>
{`import { defineSkill, z } from "@omni/sdk";

export default defineSkill({
  name: "weather-pro",
  description: "Hyperlocal forecast",
  input: z.object({
    place: z.string(),
    days:  z.number().default(3),
  }),
  async run({ place, days }, ctx) {
    const data = await ctx.tools.fetch(\`/api/wx/\${place}\`);
    await ctx.memory.write({
      key: "wx:last_place",
      value: place,
    });
    return ctx.respond.markdown(data, { days });
  },
});`}
        </pre>
        <div style={{
          padding: "10px 14px", borderTop: "1px solid var(--line)",
          background: "var(--bg-card)", display: "flex", alignItems: "center", gap: 10,
        }}>
          <span className="mono" style={{ fontSize: 11, color: "var(--success)" }}>● ready</span>
          <span className="mono" style={{ fontSize: 11, color: "var(--fg-faint)" }}>$ omni publish</span>
        </div>
      </div>
    );
  }

  // teams
  const rows: TeamRow[] = [
    { l: "Auth", k: "Okta · SAML 2.0", v: "247 users · 6 groups" },
    { l: "Compliance", k: "SOC 2 · GDPR · HIPAA", v: "audit trail enabled" },
    { l: "Data", k: "vpc-prod / eu-west-1", v: "0 egress · own KMS" },
    { l: "Agents", k: "32 deployed", v: "12k runs / day" },
  ];

  return (
    <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14, height: "100%" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div className="mono" style={{ fontSize: 10, color: "var(--accent)", letterSpacing: ".16em", textTransform: "uppercase" }}>omni · self-hosted</div>
        <span className="mono" style={{ fontSize: 11, color: "var(--success)" }}>● healthy</span>
      </div>
      {rows.map(r => (
        <div key={r.l} style={{ border: "1px solid var(--line)", padding: 14, borderRadius: 8, background: "var(--bg-card)" }}>
          <div className="mono" style={{ fontSize: 10, color: "var(--fg-faint)", letterSpacing: ".1em", textTransform: "uppercase" }}>{r.l}</div>
          <div style={{ fontSize: 13.5, color: "var(--fg)", marginTop: 4, fontWeight: 500 }}>{r.k}</div>
          <div className="mono" style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 2 }}>{r.v}</div>
        </div>
      ))}
    </div>
  );
}

export function AudienceSwitcher() {
  const [tab, setTab] = React.useState<number>(0);
  const a = AUDIENCES[tab];

  return (
    <section id="audiences" className="section">
      <div className="container">
        <div className="section__head section__head--center">
          <div className="eyebrow eyebrow--accent">Built for three audiences</div>
          <h2 className="section__title">A different surface for everyone who gets things done.</h2>
        </div>
        <div className="aud">
          <div className="aud__tabs" role="tablist">
            {AUDIENCES.map((x, i) => (
              <button key={x.key} role="tab" aria-selected={i === tab} className={`aud__tab ${i === tab ? "active" : ""}`} onClick={() => setTab(i)}>
                <span className="aud__tab-eye">{x.eye}</span>
                <span className="aud__tab-name">{x.name}</span>
              </button>
            ))}
          </div>
          <div className="aud__panel">
            <div className="aud__panel-copy">
              <div className="eyebrow eyebrow--accent">{a.eye}</div>
              <h3>{a.title}</h3>
              <p>{a.desc}</p>
              <ul className="aud__panel-bullets">
                {a.bullets.map(b => <li key={b}>{b}</li>)}
              </ul>
              <div style={{ display: "flex", gap: 10 }}>
                <a className="btn btn--primary btn--sm" href="#">Learn more <Icon name="arrow" size={13} /></a>
                <a className="btn btn--ghost btn--sm" href="#">See a demo</a>
              </div>
            </div>
            <div className="aud__panel-visual">
              <AudVisual which={a.visual} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
